import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BORDER_RADIUS, SPACING_ELEMENT, SPACING_SECTION, SPACING_CARD_BODY, SPACING_CARD, SPACING_BUTTON, SPACING_COMPONENT, SPACING_GROUP } from '../constants/layout';
import {
  FONT_TITLE,
  FONT_TITLE_MOBILE,
  FONT_TITLE3,
  FONT_BODY,
  FONT_FOOTNOTE,
} from '../constants/typography';

export default function TermsOfServiceView() {
  useEffect(() => {
    document.title = 'Terms of Service - Koral Krush';
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div className="legal-page">
        <article className="legal-article">
          <header className="legal-header">
            <h1 className="legal-title">Terms of Service</h1>
            <p className="legal-date">Effective date: April 6, 2026</p>
          </header>

          <div className="legal-body">
            <p>
              Welcome to Koral Krush. These Terms of Service ("Terms") govern your use of the Koral Krush website and services (the "Service"). By using the Service, you agree to these Terms. If you do not agree, do not use the Service.
            </p>

            <h2>1. Eligibility</h2>
            <p>
              You must be at least 13 years old to use the Service. By using the Service, you represent that you meet this requirement. If you are under 18, you may only use the Service with the involvement of a parent or guardian.
            </p>

            <h2>2. Account Registration</h2>
            <p>
              You can create an account using an email address and password. You are responsible for providing accurate information, keeping your credentials secure, and all activity that occurs under your account. Notify us immediately if you suspect unauthorized access.
            </p>
            <p>
              You can delete your account at any time from your account settings. Account deletion is permanent: your profile, bookmarks, and saved preferences will be removed. Order records are anonymized and retained for our business records. See our{' '}<Link to="/privacy">Privacy Policy</Link> for details on what happens to your data.
            </p>

            <h2>3. Products and Pricing</h2>
            <p>
              Product descriptions, images, and prices on the Service are subject to change without notice. We make reasonable efforts to display accurate information, but we do not guarantee that descriptions, photographs, or pricing are error-free. Product images may vary slightly from the actual item due to photography, screen settings, and natural variation.
            </p>
            <p>
              We reserve the right to limit order quantities, refuse any order, or cancel orders due to product availability, pricing errors, or suspected fraud. Prices are listed in the store's configured currency and do not include applicable taxes, which are calculated at checkout based on the store's tax rate.
            </p>

            <h2>4. Orders and Payment</h2>
            <p>
              When you place an order, you are making an offer to purchase. All payments are processed securely by our third-party payment processor. Your payment card details are handled entirely by the processor and never touch our servers. By submitting payment, you represent that you are authorized to use the payment method provided.
            </p>
            <p>
              Depending on the store's configuration, your payment may be captured immediately at checkout or authorized and captured later when your order is fulfilled. You will receive an order confirmation email once your payment is successfully processed.
            </p>
            <p>
              We reserve the right to cancel an order after it has been placed. If we cancel your order, you will be notified by email and any payment already captured will be refunded.
            </p>

            <h2>5. Fulfillment</h2>
            <p>
              Orders are fulfilled through local pickup. Estimated fulfillment times are provided for convenience and are not guaranteed.
            </p>
            <p>
              You may designate another person to pick up on your behalf by providing their name, phone number, and email at checkout. You are responsible for ensuring the designated person is available and informed.
            </p>

            <h2>6. Returns and Refunds</h2>
            <p>
              We accept returns of unused, undamaged items in original packaging within 30 days of purchase. To initiate a return, contact us at{' '}<a href="mailto:support@koralkrush.com">support@koralkrush.com</a>. Refunds are issued to the original payment method within 5–10 business days of receiving the returned item.
            </p>

            <h2>7. User Content</h2>
            <p>
              You may submit product reviews that include a rating, title, written text, and up to five images. Your first name will be displayed publicly with your review. By submitting a review, you grant Koral Krush a non-exclusive, royalty-free license to display your review content on the Service.
            </p>
            <p>
              We reserve the right to remove or moderate reviews at our discretion, including reviews that contain offensive content, spam, or are unrelated to the product.
            </p>

            <h2>8. Shopping Cart and Bookmarks</h2>
            <p>
              Your shopping cart is stored locally in your browser and is not synced across devices or to our servers until you proceed to checkout. If you clear your browser data, your cart will be lost.
            </p>
            <p>
              Bookmarked products are saved to your account and will persist across devices as long as you are signed in.
            </p>

            <h2>9. Intellectual Property</h2>
            <p>
              All content on the Service, including text, graphics, logos, product images, and software, is the property of Koral Krush or its licensors and is protected by copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from any content without our prior written consent.
            </p>

            <h2>10. Prohibited Conduct</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for any unlawful purpose</li>
              <li>Interfere with or disrupt the Service or its infrastructure</li>
              <li>Attempt to gain unauthorized access to any part of the Service, other users' accounts, or our systems</li>
              <li>Use bots, scrapers, or other automated means to access or extract data from the Service</li>
              <li>Place fraudulent orders or misuse the checkout process</li>
              <li>Submit false, misleading, or abusive product reviews</li>
              <li>Impersonate any person or entity</li>
              <li>Upload or transmit viruses, malware, or other harmful code</li>
            </ul>

            <h2>11. Disclaimer of Warranties</h2>
            <p>
              The Service is provided "as is" and "as available" without warranties of any kind, whether express or implied, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or secure.
            </p>

            <h2>12. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, Koral Krush shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Service, including but not limited to loss of profits, data, or goodwill. Our total liability for any claim arising from or related to the Service shall not exceed the amount you paid to Koral Krush in the 12 months preceding the claim.
            </p>

            <h2>13. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Koral Krush and its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorneys' fees) arising from your use of the Service, your violation of these Terms, or your violation of any third party's rights.
            </p>

            <h2>14. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. When we do, we will revise the effective date at the top of this page. Your continued use of the Service after changes take effect constitutes your acceptance of the revised Terms.
            </p>

            <h2>15. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the State of California, without regard to conflict of law principles. Any disputes arising under these Terms shall be resolved exclusively in the state or federal courts located in Los Angeles County, California.
            </p>

            <h2>16. Contact Us</h2>
            <p>
              If you have questions about these Terms, contact us at{' '}
              <a href="mailto:support@koralkrush.com">support@koralkrush.com</a>.
            </p>

            <div className="legal-separator" />

            <p className="legal-related">
              See also: <Link to="/privacy">Privacy Policy</Link>
            </p>
          </div>
        </article>
      </div>

      <style jsx>{`
        .legal-page {
          background: var(--color-background);
          padding: ${SPACING_SECTION} ${SPACING_CARD_BODY} 96px;
          min-height: 60vh;
        }

        @supports (padding: max(0px)) {
          .legal-page {
            padding-left: max(${SPACING_CARD_BODY}, env(safe-area-inset-left));
            padding-right: max(${SPACING_CARD_BODY}, env(safe-area-inset-right));
          }
        }

        .legal-article {
          max-width: 680px;
          margin: 0 auto;
        }

        .legal-header {
          margin-bottom: ${SPACING_SECTION};
        }

        .legal-title {
          font-size: ${FONT_TITLE};
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 0 0 ${SPACING_ELEMENT};
          letter-spacing: -0.02em;
        }

        .legal-date {
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-secondary);
          margin: 0;
        }

        .legal-body h2 {
          font-size: ${FONT_TITLE3};
          font-weight: 600;
          color: var(--color-text-primary);
          margin: ${SPACING_SECTION} 0 ${SPACING_COMPONENT};
        }

        .legal-body p {
          font-size: ${FONT_BODY};
          color: var(--color-text-secondary);
          line-height: 1.75;
          margin: 0 0 ${SPACING_BUTTON};
        }

        .legal-body ul {
          margin: 0 0 ${SPACING_BUTTON};
          padding-left: ${SPACING_CARD_BODY};
          color: var(--color-text-secondary);
        }

        .legal-body li {
          font-size: ${FONT_BODY};
          line-height: 1.75;
          margin-bottom: 4px;
        }

        .legal-body a {
          color: var(--color-accent);
          text-decoration: none;
        }

        .legal-body a:hover {
          text-decoration: underline;
        }

        .legal-separator {
          border: none;
          border-top: 1px solid var(--color-border);
          margin: ${SPACING_SECTION} 0 ${SPACING_CARD_BODY};
        }

        .legal-related {
          font-size: ${FONT_FOOTNOTE};
          color: var(--color-text-secondary);
        }

        @media (max-width: 600px) {
          .legal-page {
            padding: ${SPACING_GROUP} ${SPACING_BUTTON} 64px;
          }

          .legal-title {
            font-size: ${FONT_TITLE_MOBILE};
          }

          .legal-header {
            margin-bottom: ${SPACING_CARD};
          }

          .legal-body h2 {
            margin-top: ${SPACING_GROUP};
          }
        }
      `}</style>
    </>
  );
}
